import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./AdminWorkerWorkHours.css"; 
import bfLogo from "../assets/bf_logo.png";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { db, auth } from "../firebase";
import { doc, setDoc, getDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";

const STATUS_I18N = {
  laisva: "off",
  serga: "sick",
  atostogos: "vacation",
  svente: "holiday"
};

const pad = (n) => String(n).padStart(2, "0");
const getDaysInMonth = (y, m0) => new Date(y, m0 + 1, 0).getDate();
const latinize = (str) => { 
  if (!str) return ""; 
  return str
    .replace(/[ąĄ]/g, 'a').replace(/[čČ]/g, 'c').replace(/[ęĘ]/g, 'e')
    .replace(/[ėĖ]/g, 'e').replace(/[įĮ]/g, 'i').replace(/[šŠ]/g, 's')
    .replace(/[ųŲ]/g, 'u').replace(/[ūŪ]/g, 'u').replace(/[žŽ]/g, 'z'); 
};

const BASE_COLS = ["start", "pause", "end", "objekt", "sonstiges"];
const calculateNettoSimple = (start, end, pause) => { 
  if (!start || !end) return ""; 
  const [h1, m1] = start.split(":").map(Number); 
  const [h2, m2] = end.split(":").map(Number); 
  const startMin = h1 * 60 + m1; 
  const endMin = h2 * 60 + m2; 
  let diff = endMin - startMin; 
  if (pause) { 
    const [ph, pm] = pause.split(":").map(Number); 
    const pauseMin = ph * 60 + pm; 
    diff -= pauseMin; 
  } 
  if (diff < 0) return "00:00"; 
  const rh = Math.floor(diff / 60); 
  const rm = diff % 60; 
  return `${pad(rh)}:${pad(rm)}`; 
};

export default function AdminWorkerWorkHours() {
  const navigate = useNavigate();
  const { workerId } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const inputRefs = useRef({});
  const [rows, setRows] = useState([]);
  const [customCols, setCustomCols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workerName, setWorkerName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [newColName, setNewColName] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) setUserData(userSnap.data());
      } else { navigate("/admin/login"); }
    });
    return () => unsubscribe();
  }, [navigate]);

  const masterId = userData?.belongsTo || currentUser?.uid;

  const { year, monthIndex0, monthId } = useMemo(() => {
    const now = new Date();
    const sp = new URLSearchParams(location.search);
    const y = Number(sp.get("year")) || now.getFullYear();
    const m0 = (Number(sp.get("month")) || now.getMonth() + 1) - 1;
    return { year: y, monthIndex0: m0, monthId: `${y}_${pad(m0 + 1)}` };
  }, [location.search]);

  const monthLabel = useMemo(() => {
    const d = new Date(year, monthIndex0, 1);
    return d.toLocaleString(i18n.language, { month: "long", year: "numeric" });
  }, [year, monthIndex0, i18n.language]);

  useEffect(() => {
    if (!workerId) return;
    getDoc(doc(db, "workers", workerId)).then(s => setWorkerName(s.exists() ? s.data().name : workerId));
  }, [workerId]);

  useEffect(() => {
    if (!workerId || !masterId) return;
    const daysCount = getDaysInMonth(year, monthIndex0);
    const docId = `${workerId}_${monthId}`;
    
    const unsubscribe = onSnapshot(doc(db, "monthlyWorkHours", docId), (docSnap) => {
      let baseRows = Array.from({ length: daysCount }, (_, i) => ({ day: i + 1, start: "", pause: "", end: "", objekt: "", sonstiges: "", netto: "", custom: {} }));
      let loadedCustomCols = [];

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.adminId === masterId) {
          const savedData = data.days || {};
          loadedCustomCols = data.customCols || [];
          baseRows = baseRows.map(r => {
            const savedRow = savedData[r.day.toString()] || {};
            let extra = savedRow.sonstiges || "";
            
            // PATAISYMAS: Rodome statusą TIK jei tai nėra "darbas"
            if (savedRow.status && savedRow.status !== 'darbas') {
                if (STATUS_I18N[savedRow.status]) {
                    extra = t(STATUS_I18N[savedRow.status]).toUpperCase();
                } else {
                    extra = savedRow.status.toUpperCase();
                }
            }

            const mergedRow = { ...r, ...savedRow, sonstiges: extra, custom: savedRow.custom || {} };
            if (mergedRow.start && mergedRow.end) mergedRow.netto = calculateNettoSimple(mergedRow.start, mergedRow.end, mergedRow.pause);
            return mergedRow;
          });
        }
      }
      setRows(baseRows); setCustomCols(loadedCustomCols); setLoading(false);
    });
    return () => unsubscribe();
  }, [workerId, year, monthIndex0, monthId, masterId, t]);

  const calculateTotals = () => {
      let mins = 0; 
      const cSums = {}; 
      const cUnits = {}; 

      customCols.forEach(c => { 
        cSums[c.id] = 0; 
        cUnits[c.id] = "";
      });

      rows.forEach(r => {
          if (r.netto && r.netto.includes(":")) { 
              const [h, m] = r.netto.split(":").map(Number); 
              mins += (h * 60) + m; 
          }
          
          customCols.forEach(c => { 
              let rawVal = r.custom[c.id];
              if (rawVal) {
                  let strVal = String(rawVal);
                  if (!cUnits[c.id]) {
                      const detectedUnit = strVal.replace(/[0-9.,\s-]/g, '');
                      if (detectedUnit) cUnits[c.id] = detectedUnit;
                  }
                  strVal = strVal.replace(',', '.').replace(/[^\d.-]/g, '');
                  const val = parseFloat(strVal);
                  if (!isNaN(val)) {
                      cSums[c.id] += val; 
                  }
              }
          });
      });

      const finalCustom = {};
      customCols.forEach(c => {
          const sum = cSums[c.id];
          const cleanSum = parseFloat(sum.toFixed(2)); 
          finalCustom[c.id] = cUnits[c.id] ? `${cleanSum} ${cUnits[c.id]}` : cleanSum;
      });

      return { netto: `${Math.floor(mins / 60)}:${pad(mins % 60)}`, custom: finalCustom };
  };

  const saveToFirebase = async () => {
      if (!masterId) return;
      setLoading(true); setShowMenu(false);
      try {
          const days = {};
          rows.forEach(r => {
             const hasC = r.custom && Object.values(r.custom).some(v => v !== "");
             if(r.start || r.end || r.objekt || r.sonstiges || hasC) {
                  days[r.day.toString()] = { 
                    start: r.start || "", end: r.end || "", pause: r.pause || "", 
                    objekt: r.objekt || "", sonstiges: r.sonstiges || "", 
                    netto: calculateNettoSimple(r.start, r.end, r.pause), custom: r.custom || {} 
                  };
             }
          });
          const docId = `${workerId}_${monthId}`;
          await setDoc(doc(db, "monthlyWorkHours", docId), { adminId: masterId, workerId, year, month: pad(monthIndex0 + 1), monthId, days, customCols, updatedAt: Date.now() }, { merge: true });
          alert("✅ " + t('saved'));
      } catch (e) { alert(t('error')); }
      setLoading(false);
  };

  const getAddress = () => {
    const a = userData?.address || {};
    return `${a.street || ""}, ${a.zip || ""} ${a.city || ""}`.trim();
  };

  const exportToExcel = () => { 
    const tts = calculateTotals();
    const address = getAddress();
    
    const info = [
      [t('timesheet_title') || "Arbeitszeitnachweis / Time Sheet"], 
      [`${t('company') || "Firma"}:`, userData?.companyName || ""],
      [`${t('address') || "Adresse"}:`, address],
      [`${t('worker') || "Arbeitnehmer"}:`, workerName],
      [`${t('month') || "Monat"}:`, monthLabel],
      [], 
      [t('day'), t('workStart'), t('workEnd'), t('breakTime'), t('netto'), t('project'), t('other'), ...customCols.map(c => c.label)]
    ];
    const dataRows = rows.map(r => [
      r.day, r.start, r.end, r.pause, r.netto, r.objekt, r.sonstiges,
      ...customCols.map(c => r.custom[c.id] || "")
    ]);
    const totalRow = ["", "", "", t('total') + ":", tts.netto, "", "", ...customCols.map(c => tts.custom[c.id] || 0)];
    
    const ws = XLSX.utils.aoa_to_sheet([...info, ...dataRows, totalRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "WorkHours");
    XLSX.writeFile(wb, `${latinize(workerName)}_${monthId}.xlsx`); 
  };

  const exportToPDF = () => { 
    const pdf = new jsPDF('landscape'); 
    const tts = calculateTotals();
    const address = getAddress();

    pdf.setFontSize(18);
    pdf.setTextColor(0, 0, 0);
    pdf.text(latinize(t('timesheet_title') || "Arbeitszeitnachweis / Time Sheet"), 14, 20);
    
    pdf.setFontSize(11);
    pdf.setTextColor(50, 50, 50);
    pdf.text(`${latinize(t('company') || "Firma")}: ${latinize(userData?.companyName || "-")}`, 14, 30);
    pdf.text(`${latinize(t('address') || "Adresse")}: ${latinize(address)}`, 14, 37);
    pdf.text(`${latinize(t('worker') || "Arbeitnehmer")}: ${latinize(workerName)}`, 14, 44);
    pdf.text(`${latinize(t('month') || "Monat")}: ${latinize(monthLabel)}`, 14, 51);

    const heads = [[
        latinize(t('day')), 
        latinize(t('workStart')), 
        latinize(t('workEnd')), 
        latinize(t('breakTime')), 
        latinize(t('netto')), 
        latinize(t('project')), 
        latinize(t('other')), 
        ...customCols.map(c => latinize(c.label))
    ]];

    const body = rows.map(r => [
      r.day, 
      r.start, 
      r.end, 
      r.pause, 
      r.netto, 
      latinize(r.objekt), 
      latinize(r.sonstiges),
      ...customCols.map(c => r.custom[c.id] || "")
    ]);

    const totalRow = [
      { content: latinize(t('total').toUpperCase()), colSpan: 4, styles: { halign: 'right', fillColor: [220, 220, 220], fontStyle: 'bold', textColor: [0,0,0] } },
      { content: tts.netto, styles: { fillColor: [220, 220, 220], fontStyle: 'bold', textColor: [0,0,0] } },
      { content: "", colSpan: 2, styles: { fillColor: [220, 220, 220] } },
      ...customCols.map(c => ({ content: String(tts.custom[c.id] || 0), styles: { fillColor: [220, 220, 220], fontStyle: 'bold', textColor: [0,0,0] } }))
    ];
    body.push(totalRow);

    autoTable(pdf, { 
      head: heads, 
      body: body, 
      startY: 60, 
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontStyle: 'bold' }, 
      styles: { 
        fontSize: 8, 
        cellPadding: 2, 
        lineColor: [80, 80, 80], 
        lineWidth: 0.1,
        textColor: [0, 0, 0] 
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245] 
      }
    });

    pdf.save(`${latinize(workerName)}_${monthId}.pdf`); 
  };

  const handleEmail = () => { window.location.href = `mailto:?subject=${encodeURIComponent(t('email_subject'))}&body=${encodeURIComponent(t('email_greeting'))}`; };
  const handleKey = (e, r, c) => { if (!["Enter", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return; e.preventDefault(); let nr = r, nc = c; if (e.key === "Enter" || e.key === "ArrowDown") nr++; if (e.key === "ArrowUp") nr--; if (e.key === "ArrowRight") nc++; if (e.key === "ArrowLeft") nc--; const ref = inputRefs.current[`${nr}-${nc}`]; if (ref) ref.focus(); };
  const changeMonth = (off) => { const d = new Date(year, monthIndex0, 1); d.setMonth(d.getMonth() + off); navigate(`${location.pathname}?year=${d.getFullYear()}&month=${d.getMonth() + 1}`); };
  const updateCell = (ri, f, v, cid) => { setRows(p => p.map((r, i) => { if (i !== ri) return r; const row = { ...r, custom: { ...r.custom } }; if (cid) row.custom[cid] = v; else row[f] = v; if (['start', 'end', 'pause'].includes(f)) row.netto = calculateNettoSimple(row.start, row.end, row.pause); return row; })); };
  const addColumn = () => { if (!newColName.trim()) return; const id = `c_${Date.now()}`; setCustomCols(p => [...p, { id, label: newColName }]); setRows(r => r.map(row => ({ ...row, custom: { ...row.custom, [id]: "" } }))); setShowModal(false); setNewColName(""); };

  const removeColumn = (id) => {
    if (!window.confirm(t('sureDelete') || "Ar tikrai norite ištrinti šį stulpelį?")) return;
    setCustomCols(prev => prev.filter(c => c.id !== id));
    setRows(prevRows => prevRows.map(row => {
      const newCustom = { ...row.custom };
      delete newCustom[id];
      return { ...row, custom: newCustom };
    }));
  };

  if (loading) return <div className="loading-scr">Loading...</div>;

  return (
    <div className="awh-page">
      <button className="back-btn" onClick={() => navigate("/admin/workhours")}>← {t('back')}</button>
      <img src={bfLogo} className="awh-logo" alt="BF" />
      <h1 className="awh-title">{t('worker')}: {workerName || workerId}</h1>
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', marginBottom: '20px', background: '#161b22', padding: '15px', borderRadius: '12px', border: '1px solid #30363d'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '20px', fontSize: '18px', fontWeight: 'bold'}}>
              <button onClick={() => changeMonth(-1)} style={{background: '#21262d', color: 'white', border: '1px solid #30363d', padding: '5px 15px', borderRadius: '6px', cursor: 'pointer'}}>{"<"}</button>
              <span style={{textTransform: 'capitalize'}}>{monthLabel}</span>
              <button onClick={() => changeMonth(1)} style={{background: '#21262d', color: 'white', border: '1px solid #30363d', padding: '5px 15px', borderRadius: '6px', cursor: 'pointer'}}>{">"}</button>
          </div>
          <div style={{position: 'relative'}}>
              <button onClick={() => setShowMenu(!showMenu)} style={{background: '#3b82f6', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px'}}>{t('actions')} ▼</button>
              {showMenu && (<div style={{position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)', background: '#1e293b', border: '1px solid #475569', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)', zIndex: 1000, overflow: 'hidden', minWidth: '180px'}}>
                <button onClick={saveToFirebase} style={{display: 'block', width: '100%', padding: '12px', textAlign: 'left', background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', borderBottom: '1px solid #334155'}}>💾 {t('save')}</button>
                <button onClick={exportToExcel} style={{display: 'block', width: '100%', padding: '12px', textAlign: 'left', background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', borderBottom: '1px solid #334155'}}>📊 Excel</button>
                <button onClick={exportToPDF} style={{display: 'block', width: '100%', padding: '12px', textAlign: 'left', background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', borderBottom: '1px solid #334155'}}>📄 PDF</button>
                <button onClick={handleEmail} style={{display: 'block', width: '100%', padding: '12px', textAlign: 'left', background: 'transparent', color: '#38bdf8', border: 'none', cursor: 'pointer'}}>✉️ Email</button>
              </div>)}
          </div>
      </div>
      <button className="btn-add" onClick={() => setShowModal(true)}>+ {t('add_column')}</button>
      <div className="table-wrapper">
        <table className="awh-table">
          <thead>
            <tr>
                <th>{t('day')}</th>
                <th>{t('workStart')}</th>
                <th>{t('breakTime')}</th>
                <th>{t('workEnd')}</th>
                <th>{t('project')}</th>
                <th>{t('other')}</th>
                <th>{t('netto')}</th>
                {customCols.map(c => (
                  <th key={c.id} className="custom-th">
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'}}>
                      {c.label}
                      <Trash2 
                        size={14} 
                        style={{cursor: 'pointer', color: '#ff7b72'}} 
                        onClick={() => removeColumn(c.id)}
                      />
                    </div>
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.day}</td>
                {BASE_COLS.map((c, ci) => (<td key={c}><input ref={(el) => (inputRefs.current[`${i}-${ci}`] = el)} value={r[c]} onChange={(e) => updateCell(i, c, e.target.value)} onKeyDown={(e) => handleKey(e, i, ci)} /></td>))}
                <td className="netto-cell">{r.netto}</td>
                {customCols.map((c, ci) => { const colIndex = BASE_COLS.length + ci; return (<td key={c.id}><input ref={(el) => (inputRefs.current[`${i}-${colIndex}`] = el)} value={r.custom[c.id]} onChange={(e) => updateCell(i, null, e.target.value, c.id)} onKeyDown={(e) => handleKey(e, i, colIndex)} /></td>); })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="summary" style={{marginTop: '20px', padding: '15px', background: '#161b22', borderRadius: '8px', border: '1px solid #30363d', color: '#ffffff', display: 'flex', gap: '30px', fontSize: '16px', fontWeight: 'bold'}}><div style={{color: '#58a6ff'}}>{t('total_netto')}: {calculateTotals().netto}</div>{customCols.map((c) => <div key={c.id} style={{color: '#3fb950'}}>{c.label}: {calculateTotals().custom[c.id]}</div>)}</div>
      {showModal && (<div className="modal-backdrop" onKeyDown={(e) => { if (e.key === "Escape") setShowModal(false); if (e.key === "Enter") addColumn(); }}><div className="modal"><h3>{t('add_column')}</h3><input autoFocus value={newColName} onChange={(e) => setNewColName(e.target.value)} /><div className="modal-actions"><button onClick={addColumn}>OK</button><button onClick={() => setShowModal(false)}>{t('cancel')}</button></div></div></div>)}
    </div>
  );
}